import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  ChevronRight, 
  ChevronLeft,
  Menu, 
  X, 
  ArrowLeft,
  Check,
  User,
  Users,
  Trophy,
  Heart,
  Upload,
  AlertCircle,
  CheckCircle2,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Navigation Component
const BewerbungNavigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5 ${isScrolled ? "py-3" : "py-4"} transition-all duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/akademie" className="flex items-center gap-3">
            <img 
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_coach-reserve-2/artifacts/2ayyxywl_fu%C3%9Fballakademie_logo.png" 
              alt="Aktas Fussballakademie"
              className="h-12 w-12 object-contain"
            />
            <div className="hidden sm:block">
              <p className="font-['Bebas_Neue'] text-lg text-white leading-tight">COACH YASIN</p>
              <p className="text-[10px] text-[#bea777] tracking-[0.2em]">AKTAS FUSSBALLAKADEMIE</p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <Link to="/akademie" className="text-[#A1A1AA] hover:text-white text-sm transition-colors flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Akademie
            </Link>
          </div>

          <button className="lg:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
            <Link to="/akademie" className="block text-[#bea777] hover:text-white py-2" onClick={() => setIsMobileMenuOpen(false)}>
              ← Zurück zur Akademie
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Step Indicator
const StepIndicator = ({ currentStep }) => {
  const steps = [
    { num: 1, label: "Spieler", icon: User },
    { num: 2, label: "Sportlich", icon: Trophy },
    { num: 3, label: "Eltern", icon: Users },
    { num: 4, label: "Motivation", icon: Heart },
    { num: 5, label: "Absenden", icon: Check }
  ];

  return (
    <div className="flex justify-between items-center mb-8 px-4">
      {steps.map((step, index) => (
        <div key={step.num} className="flex items-center">
          <div className={`flex flex-col items-center ${currentStep >= step.num ? 'text-[#bea777]' : 'text-[#71717A]'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
              currentStep > step.num 
                ? 'bg-[#bea777] border-[#bea777] text-[#0A0A0A]' 
                : currentStep === step.num 
                  ? 'border-[#bea777] text-[#bea777]' 
                  : 'border-[#71717A]'
            }`}>
              {currentStep > step.num ? (
                <Check className="w-5 h-5" />
              ) : (
                <step.icon className="w-5 h-5" />
              )}
            </div>
            <span className="text-xs mt-2 hidden sm:block">{step.label}</span>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-8 sm:w-16 h-0.5 mx-1 sm:mx-2 ${currentStep > step.num ? 'bg-[#bea777]' : 'bg-[#71717A]'}`} />
          )}
        </div>
      ))}
    </div>
  );
};

// Success Screen
const SuccessScreen = ({ bewerbungId, spielerName }) => (
  <div className="text-center py-12">
    <div className="w-20 h-20 bg-[#bea777]/20 rounded-full flex items-center justify-center mx-auto mb-6">
      <CheckCircle2 className="w-12 h-12 text-[#bea777]" />
    </div>
    <h2 className="font-['Bebas_Neue'] text-4xl text-white mb-4">BEWERBUNG EINGEREICHT!</h2>
    <p className="text-[#A1A1AA] text-lg mb-6">
      Vielen Dank für die Bewerbung von <span className="text-white font-medium">{spielerName}</span>.
    </p>
    <div className="bg-[#1A1A1A] border border-white/10 p-6 max-w-md mx-auto mb-8">
      <p className="text-[#71717A] text-sm mb-2">Deine Bewerbungs-ID</p>
      <p className="font-mono text-[#bea777] text-xl">{bewerbungId}</p>
    </div>
    <div className="bg-[#1A1A1A] border border-[#bea777]/30 p-6 max-w-lg mx-auto mb-8">
      <h3 className="text-white font-semibold mb-3">Was passiert jetzt?</h3>
      <ul className="text-left text-[#A1A1AA] space-y-2">
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 text-[#bea777] mt-1 flex-shrink-0" />
          <span>Wir prüfen deine Bewerbung innerhalb von 7 Tagen</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 text-[#bea777] mt-1 flex-shrink-0" />
          <span>Bei Interesse laden wir zu einem Sichtungstraining ein</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 text-[#bea777] mt-1 flex-shrink-0" />
          <span>Du erhältst alle Infos per E-Mail</span>
        </li>
      </ul>
    </div>
    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <Button asChild className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] font-semibold px-8 py-6 rounded-none">
        <Link to="/akademie">Zurück zur Akademie</Link>
      </Button>
      <Button asChild variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-none">
        <Link to="/">Zur Hauptseite</Link>
      </Button>
    </div>
  </div>
);

// Main Form Component
export default function BewerbungPage() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    spieler: {
      vorname: "",
      nachname: "",
      geburtsdatum: "",
      jahrgang: "",
      groesse: "",
      trikotgroesse: "",
      hosengroesse: "",
      aktuellerVerein: "",
      position: "",
      istTorwart: false,
      spielfuss: "",
      trikotnummer: ""
    },
    sportlicherHintergrund: {
      vereinshistorie: "",
      erfolge: "",
      staerken: "",
      schwaechen: "",
      trainingshaeufigkeit: ""
    },
    eltern: {
      vorname: "",
      nachname: "",
      email: "",
      telefon: "",
      strasse: "",
      hausnummer: "",
      plz: "",
      ort: "",
      verhaeltnis: ""
    },
    motivation: "",
    videoUrl: "",
    gewaehltesPaket: "",
    datenschutzAkzeptiert: false,
    regelnAkzeptiert: false
  });

  useEffect(() => {
    setIsLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const updateFormData = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    setErrors(prev => ({ ...prev, [`${section}.${field}`]: null, [field]: null }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.spieler.vorname) newErrors["spieler.vorname"] = "Vorname erforderlich";
        if (!formData.spieler.nachname) newErrors["spieler.nachname"] = "Nachname erforderlich";
        if (!formData.spieler.geburtsdatum) newErrors["spieler.geburtsdatum"] = "Geburtsdatum erforderlich";
        if (!formData.spieler.jahrgang) newErrors["spieler.jahrgang"] = "Jahrgang erforderlich";
        if (!formData.spieler.position) newErrors["spieler.position"] = "Position erforderlich";
        if (!formData.spieler.spielfuss) newErrors["spieler.spielfuss"] = "Spielfuß erforderlich";
        break;
      case 2:
        if (!formData.sportlicherHintergrund.trainingshaeufigkeit) {
          newErrors["sportlicherHintergrund.trainingshaeufigkeit"] = "Trainingshäufigkeit erforderlich";
        }
        break;
      case 3:
        if (!formData.eltern.vorname) newErrors["eltern.vorname"] = "Vorname erforderlich";
        if (!formData.eltern.nachname) newErrors["eltern.nachname"] = "Nachname erforderlich";
        if (!formData.eltern.email) newErrors["eltern.email"] = "E-Mail erforderlich";
        if (!formData.eltern.telefon) newErrors["eltern.telefon"] = "Telefon erforderlich";
        if (!formData.eltern.verhaeltnis) newErrors["eltern.verhaeltnis"] = "Verhältnis erforderlich";
        if (formData.eltern.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.eltern.email)) {
          newErrors["eltern.email"] = "Ungültige E-Mail-Adresse";
        }
        break;
      case 4:
        if (!formData.motivation || formData.motivation.length < 50) {
          newErrors["motivation"] = "Motivation erforderlich (min. 50 Zeichen)";
        }
        break;
      case 5:
        if (!formData.gewaehltesPaket) {
          newErrors["gewaehltesPaket"] = "Bitte wähle ein Mitgliedschafts-Paket";
        }
        if (!formData.datenschutzAkzeptiert) {
          newErrors["datenschutzAkzeptiert"] = "Datenschutz muss akzeptiert werden";
        }
        if (!formData.regelnAkzeptiert) {
          newErrors["regelnAkzeptiert"] = "Akademie-Regeln müssen akzeptiert werden";
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      toast.error("Bitte alle Bedingungen akzeptieren");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/bewerbung/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Fehler beim Einreichen der Bewerbung");
      }

      setSubmitSuccess({
        bewerbungId: data.bewerbung_id,
        spielerName: data.spieler_name
      });
      
      toast.success("Bewerbung erfolgreich eingereicht!");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || "Fehler beim Einreichen der Bewerbung");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function for text inputs
  const renderTextInput = (label, section, field, options = {}) => {
    const { type = "text", required = false, placeholder = "" } = options;
    const errorKey = section ? `${section}.${field}` : field;
    const value = section ? formData[section][field] : formData[field];
    
    return (
      <div className="space-y-2">
        <Label className="text-white">
          {label} {required && <span className="text-[#bea777]">*</span>}
        </Label>
        <Input
          type={type}
          value={value}
          onChange={(e) => updateFormData(section, field, e.target.value)}
          placeholder={placeholder}
          className={`bg-[#1A1A1A] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#bea777] ${
            errors[errorKey] ? 'border-red-500' : ''
          }`}
        />
        {errors[errorKey] && (
          <p className="text-red-400 text-xs flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors[errorKey]}
          </p>
        )}
      </div>
    );
  };

  // Render Step Content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">SPIELERDATEN</h2>
              <p className="text-[#A1A1AA]">Informationen zum Spieler</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderTextInput("Vorname", "spieler", "vorname", { required: true, placeholder: "Max" })}
              {renderTextInput("Nachname", "spieler", "nachname", { required: true, placeholder: "Mustermann" })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderTextInput("Geburtsdatum", "spieler", "geburtsdatum", { type: "date", required: true })}
              <div className="space-y-2">
                <Label className="text-white">Jahrgang <span className="text-[#bea777]">*</span></Label>
                <Select
                  value={formData.spieler.jahrgang}
                  onValueChange={(value) => updateFormData("spieler", "jahrgang", value)}
                >
                  <SelectTrigger className={`bg-[#1A1A1A] border-white/10 text-white ${errors["spieler.jahrgang"] ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Jahrgang wählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="U8">U8 (2019)</SelectItem>
                    <SelectItem value="U9">U9 (2018)</SelectItem>
                    <SelectItem value="U10">U10 (2017)</SelectItem>
                    <SelectItem value="U11">U11 (2016)</SelectItem>
                    <SelectItem value="U12">U12 (2015)</SelectItem>
                    <SelectItem value="U13">U13 (2014)</SelectItem>
                  </SelectContent>
                </Select>
                {errors["spieler.jahrgang"] && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />{errors["spieler.jahrgang"]}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {renderTextInput("Größe (in cm)", "spieler", "groesse", { placeholder: "z.B. 145" })}
              {renderTextInput("Trikotgröße", "spieler", "trikotgroesse", { placeholder: "z.B. 140, S, M" })}
              {renderTextInput("Hosengröße", "spieler", "hosengroesse", { placeholder: "z.B. 140, S, M" })}
            </div>

            {renderTextInput("Aktueller Verein", "spieler", "aktuellerVerein", { placeholder: "FC Beispiel" })}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Position <span className="text-[#bea777]">*</span></Label>
                <Select
                  value={formData.spieler.position}
                  onValueChange={(value) => {
                    updateFormData("spieler", "position", value);
                    updateFormData("spieler", "istTorwart", value === "Torwart");
                  }}
                >
                  <SelectTrigger className={`bg-[#1A1A1A] border-white/10 text-white ${errors["spieler.position"] ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Position wählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="Torwart">Torwart</SelectItem>
                    <SelectItem value="Innenverteidiger">Innenverteidiger</SelectItem>
                    <SelectItem value="Außenverteidiger">Außenverteidiger</SelectItem>
                    <SelectItem value="Defensives Mittelfeld">Defensives Mittelfeld</SelectItem>
                    <SelectItem value="Zentrales Mittelfeld">Zentrales Mittelfeld</SelectItem>
                    <SelectItem value="Offensives Mittelfeld">Offensives Mittelfeld</SelectItem>
                    <SelectItem value="Flügelspieler">Flügelspieler</SelectItem>
                    <SelectItem value="Stürmer">Stürmer</SelectItem>
                  </SelectContent>
                </Select>
                {errors["spieler.position"] && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />{errors["spieler.position"]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-white">Spielfuß <span className="text-[#bea777]">*</span></Label>
                <Select
                  value={formData.spieler.spielfuss}
                  onValueChange={(value) => updateFormData("spieler", "spielfuss", value)}
                >
                  <SelectTrigger className={`bg-[#1A1A1A] border-white/10 text-white ${errors["spieler.spielfuss"] ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Spielfuß wählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="rechts">Rechts</SelectItem>
                    <SelectItem value="links">Links</SelectItem>
                    <SelectItem value="beidfuessig">Beidfüßig</SelectItem>
                  </SelectContent>
                </Select>
                {errors["spieler.spielfuss"] && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />{errors["spieler.spielfuss"]}
                  </p>
                )}
              </div>
            </div>

            {renderTextInput("Trikotnummer (optional)", "spieler", "trikotnummer", { placeholder: "10" })}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">SPORTLICHER HINTERGRUND</h2>
              <p className="text-[#A1A1AA]">Fußballerische Entwicklung und Erfahrung</p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Bisherige Vereine</Label>
              <Textarea
                value={formData.sportlicherHintergrund.vereinshistorie}
                onChange={(e) => updateFormData("sportlicherHintergrund", "vereinshistorie", e.target.value)}
                placeholder="z.B. 2020-2022 FC Musterstadt, 2022-heute SV Beispiel"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#bea777] min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Erfolge & Auszeichnungen</Label>
              <Textarea
                value={formData.sportlicherHintergrund.erfolge}
                onChange={(e) => updateFormData("sportlicherHintergrund", "erfolge", e.target.value)}
                placeholder="z.B. Kreismeister 2023, Torschützenkönig Hallenturnier"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#bea777] min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Stärken</Label>
                <Textarea
                  value={formData.sportlicherHintergrund.staerken}
                  onChange={(e) => updateFormData("sportlicherHintergrund", "staerken", e.target.value)}
                  placeholder="z.B. Schnelligkeit, Technik, Spielübersicht"
                  className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#bea777] min-h-[80px]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Entwicklungsfelder</Label>
                <Textarea
                  value={formData.sportlicherHintergrund.schwaechen}
                  onChange={(e) => updateFormData("sportlicherHintergrund", "schwaechen", e.target.value)}
                  placeholder="z.B. Kopfballspiel, schwacher Fuß"
                  className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#bea777] min-h-[80px]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Aktuelle Trainingshäufigkeit <span className="text-[#bea777]">*</span></Label>
              <Select
                value={formData.sportlicherHintergrund.trainingshaeufigkeit}
                onValueChange={(value) => updateFormData("sportlicherHintergrund", "trainingshaeufigkeit", value)}
              >
                <SelectTrigger className={`bg-[#1A1A1A] border-white/10 text-white ${errors["sportlicherHintergrund.trainingshaeufigkeit"] ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Wie oft trainiert der Spieler aktuell?" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="1x">1x pro Woche</SelectItem>
                  <SelectItem value="2x">2x pro Woche</SelectItem>
                  <SelectItem value="3x">3x pro Woche</SelectItem>
                  <SelectItem value="4x">4x oder mehr pro Woche</SelectItem>
                  <SelectItem value="kein_verein">Aktuell kein Vereinstraining</SelectItem>
                </SelectContent>
              </Select>
              {errors["sportlicherHintergrund.trainingshaeufigkeit"] && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />{errors["sportlicherHintergrund.trainingshaeufigkeit"]}
                </p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">ERZIEHUNGSBERECHTIGTE</h2>
              <p className="text-[#A1A1AA]">Kontaktdaten der Eltern</p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Verhältnis zum Spieler <span className="text-[#bea777]">*</span></Label>
              <Select
                value={formData.eltern.verhaeltnis}
                onValueChange={(value) => updateFormData("eltern", "verhaeltnis", value)}
              >
                <SelectTrigger className={`bg-[#1A1A1A] border-white/10 text-white ${errors["eltern.verhaeltnis"] ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Verhältnis wählen" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="Vater">Vater</SelectItem>
                  <SelectItem value="Mutter">Mutter</SelectItem>
                  <SelectItem value="Erziehungsberechtigter">Erziehungsberechtigter</SelectItem>
                </SelectContent>
              </Select>
              {errors["eltern.verhaeltnis"] && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />{errors["eltern.verhaeltnis"]}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderTextInput("Vorname", "eltern", "vorname", { required: true, placeholder: "Maria" })}
              {renderTextInput("Nachname", "eltern", "nachname", { required: true, placeholder: "Mustermann" })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderTextInput("E-Mail", "eltern", "email", { type: "email", required: true, placeholder: "maria@beispiel.de" })}
              {renderTextInput("Telefon", "eltern", "telefon", { type: "tel", required: true, placeholder: "0174 123 4567" })}
            </div>

            <div className="border-t border-white/10 pt-6 mt-6">
              <p className="text-[#71717A] text-sm mb-4">Adresse (optional)</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  {renderTextInput("Straße", "eltern", "strasse", { placeholder: "Musterstraße" })}
                </div>
                {renderTextInput("Nr.", "eltern", "hausnummer", { placeholder: "12" })}
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {renderTextInput("PLZ", "eltern", "plz", { placeholder: "12345" })}
                <div className="col-span-2">
                  {renderTextInput("Ort", "eltern", "ort", { placeholder: "Musterstadt" })}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">MOTIVATION</h2>
              <p className="text-[#A1A1AA]">Warum möchte der Spieler zur Akademie?</p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Motivation <span className="text-[#bea777]">*</span></Label>
              <Textarea
                value={formData.motivation}
                onChange={(e) => updateFormData(null, "motivation", e.target.value)}
                placeholder="Beschreibe, warum du Teil der Aktas Fussballakademie werden möchtest. Was sind deine Ziele? Was erhoffst du dir von der Förderung? (min. 50 Zeichen)"
                className={`bg-[#1A1A1A] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#bea777] min-h-[150px] ${errors["motivation"] ? 'border-red-500' : ''}`}
              />
              <div className="flex justify-between">
                {errors["motivation"] && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />{errors["motivation"]}
                  </p>
                )}
                <p className="text-[#71717A] text-xs ml-auto">{formData.motivation.length} / 50 Zeichen min.</p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6 mt-6">
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#bea777]" />
                  Video-Link (optional)
                </Label>
                <Input
                  type="url"
                  value={formData.videoUrl}
                  onChange={(e) => updateFormData(null, "videoUrl", e.target.value)}
                  placeholder="https://youtube.com/watch?v=... oder https://drive.google.com/..."
                  className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#bea777]"
                />
                <p className="text-[#71717A] text-xs">
                  Lade ein Video des Spielers hoch (YouTube, Google Drive, Dropbox). 
                  Das Video kann Spielszenen, Technikübungen oder Highlights zeigen.
                </p>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">ZUSAMMENFASSUNG</h2>
              <p className="text-[#A1A1AA]">Überprüfe deine Angaben und sende die Bewerbung ab</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#1A1A1A] border border-white/10 p-4">
                <h3 className="text-[#bea777] text-sm uppercase mb-3">Spieler</h3>
                <p className="text-white font-medium">{formData.spieler.vorname} {formData.spieler.nachname}</p>
                <p className="text-[#A1A1AA] text-sm">{formData.spieler.jahrgang} · {formData.spieler.position}</p>
                <p className="text-[#71717A] text-sm">{formData.spieler.aktuellerVerein || "Kein Verein angegeben"}</p>
              </div>
              <div className="bg-[#1A1A1A] border border-white/10 p-4">
                <h3 className="text-[#bea777] text-sm uppercase mb-3">Kontakt</h3>
                <p className="text-white font-medium">{formData.eltern.vorname} {formData.eltern.nachname}</p>
                <p className="text-[#A1A1AA] text-sm">{formData.eltern.email}</p>
                <p className="text-[#71717A] text-sm">{formData.eltern.telefon}</p>
              </div>
            </div>

            {/* Paket-Auswahl */}
            <div className="bg-[#121212] border border-[#bea777]/30 p-6">
              <h3 className="text-white font-medium mb-4">Mitgliedschaft wählen <span className="text-[#bea777]">*</span></h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Jahresvertrag */}
                <div 
                  onClick={() => updateFormData(null, "gewaehltesPaket", "jahresvertrag")}
                  className={`relative cursor-pointer border-2 p-4 transition-all ${
                    formData.gewaehltesPaket === "jahresvertrag" 
                      ? "border-[#bea777] bg-[#bea777]/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <div className="absolute -top-3 left-4 bg-[#bea777] text-[#0A0A0A] text-xs font-bold px-2 py-0.5">
                    EMPFOHLEN
                  </div>
                  <p className="text-[#bea777] text-xs uppercase mb-1">Jahresvertrag</p>
                  <p className="text-white text-2xl font-bold">64,83 EUR<span className="text-sm font-normal text-[#71717A]">/Monat</span></p>
                  <p className="text-[#71717A] text-xs mt-1">12 Monate - 778 EUR gesamt</p>
                  <p className="text-green-400 text-xs mt-1">50 EUR Ersparnis</p>
                </div>

                {/* Halbjahresvertrag */}
                <div 
                  onClick={() => updateFormData(null, "gewaehltesPaket", "halbjahresvertrag")}
                  className={`cursor-pointer border-2 p-4 transition-all ${
                    formData.gewaehltesPaket === "halbjahresvertrag" 
                      ? "border-[#bea777] bg-[#bea777]/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <p className="text-[#bea777] text-xs uppercase mb-1">Halbjahresvertrag</p>
                  <p className="text-white text-2xl font-bold">79 EUR<span className="text-sm font-normal text-[#71717A]">/Monat</span></p>
                  <p className="text-[#71717A] text-xs mt-1">6 Monate - 474 EUR gesamt</p>
                </div>

                {/* Monatsvertrag */}
                <div 
                  onClick={() => updateFormData(null, "gewaehltesPaket", "monatsvertrag")}
                  className={`cursor-pointer border-2 p-4 transition-all ${
                    formData.gewaehltesPaket === "monatsvertrag" 
                      ? "border-[#bea777] bg-[#bea777]/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <p className="text-[#bea777] text-xs uppercase mb-1">Monatsvertrag</p>
                  <p className="text-white text-2xl font-bold">89 EUR<span className="text-sm font-normal text-[#71717A]">/Monat</span></p>
                  <p className="text-[#71717A] text-xs mt-1">Monatlich kundbar</p>
                  <p className="text-[#71717A] text-xs mt-1">Maximale Flexibilitat</p>
                </div>
              </div>
              {errors["gewaehltesPaket"] && (
                <p className="text-red-400 text-xs flex items-center gap-1 mt-3">
                  <AlertCircle className="w-3 h-3" />{errors["gewaehltesPaket"]}
                </p>
              )}
              <p className="text-[#71717A] text-xs mt-4">
                Alle Pakete beinhalten: Trainings, Akademie-Ausstattung, 15% Rabatt auf Camps
              </p>
            </div>

            {/* Consent Checkboxes */}
            <div className="bg-[#121212] border border-white/5 p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="datenschutz"
                  checked={formData.datenschutzAkzeptiert}
                  onCheckedChange={(checked) => updateFormData(null, "datenschutzAkzeptiert", checked)}
                  className="mt-1 border-white/30 data-[state=checked]:bg-[#bea777] data-[state=checked]:border-[#bea777]"
                />
                <div>
                  <Label htmlFor="datenschutz" className="text-white cursor-pointer">
                    Datenschutzerklärung <span className="text-[#bea777]">*</span>
                  </Label>
                  <p className="text-[#71717A] text-sm">
                    Ich habe die <Link to="/datenschutz" target="_blank" className="text-[#bea777] hover:underline">Datenschutzerklärung</Link> gelesen 
                    und stimme der Verarbeitung meiner Daten zu.
                  </p>
                  {errors["datenschutzAkzeptiert"] && (
                    <p className="text-red-400 text-xs flex items-center gap-1 mt-1">
                      <AlertCircle className="w-3 h-3" />{errors["datenschutzAkzeptiert"]}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="regeln"
                  checked={formData.regelnAkzeptiert}
                  onCheckedChange={(checked) => updateFormData(null, "regelnAkzeptiert", checked)}
                  className="mt-1 border-white/30 data-[state=checked]:bg-[#bea777] data-[state=checked]:border-[#bea777]"
                />
                <div>
                  <Label htmlFor="regeln" className="text-white cursor-pointer">
                    Akademie-Regeln <span className="text-[#bea777]">*</span>
                  </Label>
                  <p className="text-[#71717A] text-sm">
                    Ich akzeptiere die Werte und Regeln der Aktas Fussballakademie: 
                    Disziplin, Respekt, Pünktlichkeit, Einsatzbereitschaft, Teamfähigkeit, Lernbereitschaft.
                  </p>
                  {errors["regelnAkzeptiert"] && (
                    <p className="text-red-400 text-xs flex items-center gap-1 mt-1">
                      <AlertCircle className="w-3 h-3" />{errors["regelnAkzeptiert"]}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (submitSuccess) {
    return (
      <main className={`min-h-screen bg-[#0A0A0A] transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <Helmet>
          <title>Bewerbung eingereicht | Aktas Fussballakademie</title>
          <link rel="canonical" href="https://personal-fussballtraining.de/bewerbung" />
        </Helmet>
        <BewerbungNavigation />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-2xl mx-auto">
            <SuccessScreen 
              bewerbungId={submitSuccess.bewerbungId} 
              spielerName={submitSuccess.spielerName} 
            />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={`min-h-screen bg-[#0A0A0A] transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
      <Helmet>
        <title>Jetzt bewerben | Aktas Fussballakademie</title>
        <meta name="description" content="Bewirb dich jetzt für die Aktas Fussballakademie. Leistungsorientierte Fußballförderung für Jahrgänge U8 bis U13." />
        <link rel="canonical" href="https://personal-fussballtraining.de/bewerbung" />
      </Helmet>
      
      <BewerbungNavigation />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#bea777]/10 border border-[#bea777]/30 px-4 py-2 mb-4">
              <span className="w-2 h-2 bg-[#bea777] rounded-full animate-pulse" />
              <span className="text-[#bea777] text-sm font-medium">Aufnahme 2026</span>
            </div>
            <h1 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-white mb-2">
              AKADEMIE-<span className="text-[#bea777]">BEWERBUNG</span>
            </h1>
            <p className="text-[#A1A1AA]">Aktas Fussballakademie · Jahrgänge U8 bis U13</p>
          </div>

          {/* Step Indicator */}
          <StepIndicator currentStep={currentStep} />

          {/* Form Container */}
          <div className="bg-[#0F0F0F] border border-white/5 p-6 sm:p-8">
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              <Button
                onClick={prevStep}
                disabled={currentStep === 1}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 disabled:opacity-30 px-6 py-5 rounded-none"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>

              {currentStep < 5 ? (
                <Button
                  onClick={nextStep}
                  className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] font-semibold px-6 py-5 rounded-none"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] font-semibold px-8 py-5 rounded-none"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      Bewerbung absenden
                      <Check className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
